import React, { useCallback, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Table as AntTable, Tabs, Select, Input, Button, Modal, Tag } from 'antd'
import {
  CheckOutlined,
  CloseOutlined,
  UndoOutlined,
  SearchOutlined,
  DownOutlined,
  RightOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import useAction from 'utils/useAction'
import { selectors as earnOffersSelectors } from 'domains/earnOffersManagement'
import * as actions from 'actions/views/earnOffersManagement'
import { openKarmaEarnProgramModal } from 'reducers/modals/actions'
import { PROGRAM_STATUS_OPTIONS } from 'const/earnPrograms'
import { getGameColumns, getOfferColumns, goalColumns } from './columns'
import {
  Wrapper,
  PageHeader,
  FiltersRow,
  BulkActionsRow,
  TableWrapper,
} from './EarnOffersManagement.styles'

const { Search } = Input
const { TabPane } = Tabs

const toNetworkLabel = (value) =>
  String(value)
    .split('_')
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : word))
    .join(' ')

const APPROVAL_STATUS_OPTIONS = [
  { label: 'Pending', value: 'pending' },
  { label: 'Approved', value: 'approved' },
  { label: 'Blocked', value: 'blocked' },
]

const PER_PAGE = 50

const EarnOffersManagement = () => {
  const data = useSelector(earnOffersSelectors.getData)
  const isLoaded = useSelector(earnOffersSelectors.getIsLoaded)
  const page = useSelector(earnOffersSelectors.getPage)
  const total = useSelector(earnOffersSelectors.getTotal)
  const search = useSelector(earnOffersSelectors.getSearch)
  const network = useSelector(earnOffersSelectors.getNetwork)
  const programStatus = useSelector(earnOffersSelectors.getProgramStatus)
  const approvalStatus = useSelector(earnOffersSelectors.getApprovalStatus)
  const sort = useSelector(earnOffersSelectors.getSort)
  const activeTab = useSelector(earnOffersSelectors.getActiveTab)
  const selectedRowKeys = useSelector(earnOffersSelectors.getSelectedRowKeys)
  const isBulkActionLoading = useSelector(earnOffersSelectors.getIsBulkActionLoading)
  const networks = useSelector(earnOffersSelectors.getNetworks)

  const fetchOffers = useAction(actions.fetchOffers)
  const fetchNetworks = useAction(actions.fetchNetworks)
  const fetchGameGenres = useAction(actions.fetchGameGenres)
  const setActiveTab = useAction(actions.setActiveTab)
  const setSelectedRowKeys = useAction(actions.setSelectedRowKeys)
  const bulkApprove = useAction(actions.bulkApprove)
  const bulkDecline = useAction(actions.bulkDecline)
  const bulkUndo = useAction(actions.bulkUndo)
  const clearData = useAction(actions.clearData)
  const openProgramModal = useAction(openKarmaEarnProgramModal)
  const deleteNetworkProgram = useAction(actions.deleteNetworkProgram)

  useEffect(() => {
    fetchOffers({ page: 1, offerType: 'game' })
    fetchNetworks()
    fetchGameGenres()
    return () => clearData()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const networkOptions = useMemo(
    () => networks.map((value) => ({ value, label: toNetworkLabel(value) })),
    [networks]
  )

  const handleTabChange = useCallback((key) => {
    setActiveTab(key)
    const offerType = key === 'game' ? 'game' : 'app'
    fetchOffers({ page: 1, offerType })
  }, [setActiveTab, fetchOffers])

  const handleSearch = useCallback((value) => {
    fetchOffers({ page: 1, search: value, network, programStatus, approvalStatus, sort })
  }, [fetchOffers, network, programStatus, approvalStatus, sort])

  const handleNetworkChange = useCallback((value) => {
    const networkValue = value ? value.join(',') : ''
    fetchOffers({ page: 1, search, network: networkValue, programStatus, approvalStatus, sort })
  }, [fetchOffers, search, programStatus, approvalStatus, sort])

  const handleProgramStatusChange = useCallback((value) => {
    fetchOffers({ page: 1, search, network, programStatus: value || '', approvalStatus, sort })
  }, [fetchOffers, search, network, approvalStatus, sort])

  const handleApprovalStatusChange = useCallback((value) => {
    fetchOffers({ page: 1, search, network, programStatus, approvalStatus: value || '', sort })
  }, [fetchOffers, search, network, programStatus, sort])

  const handleTableChange = useCallback((pagination, _filters, tableSorter) => {
    const newPage = pagination.current
    let newSort = ''
    if (tableSorter?.field && tableSorter?.order) {
      const fieldKey = Array.isArray(tableSorter.field)
        ? tableSorter.field[tableSorter.field.length - 1]
        : tableSorter.field
      const sortPrefix = tableSorter.order === 'descend' ? '' : '-'
      newSort = `${sortPrefix}${fieldKey}`
    }

    fetchOffers({
      page: newPage,
      search,
      network,
      programStatus,
      approvalStatus,
      sort: newSort,
    })
  }, [fetchOffers, search, network, programStatus, approvalStatus])

  // Bulk action handlers
  const selectedRows = useMemo(() => {
    return data.filter(item => selectedRowKeys.includes(item.id))
  }, [data, selectedRowKeys])

  const hasPendingSelected = useMemo(() => {
    return selectedRows.some(row => row.approval_status === 'pending')
  }, [selectedRows])

  const hasReviewedSelected = useMemo(() => {
    return selectedRows.some(row =>
      row.approval_status === 'approved' || row.approval_status === 'blocked'
    )
  }, [selectedRows])

  const handleBulkApprove = useCallback(() => {
    const pendingIds = selectedRows
      .filter(row => row.approval_status === 'pending')
      .map(row => row.id)

    Modal.confirm({
      title: 'Approve Offers',
      content: `Are you sure you want to approve ${pendingIds.length} offer(s)?`,
      onOk() {
        bulkApprove({ ids: pendingIds })
      },
    })
  }, [selectedRows, bulkApprove])

  const handleBulkDecline = useCallback(() => {
    const pendingIds = selectedRows
      .filter(row => row.approval_status === 'pending')
      .map(row => row.id)

    Modal.confirm({
      title: 'Decline Offers',
      content: `Are you sure you want to decline ${pendingIds.length} offer(s)?`,
      onOk() {
        bulkDecline({ ids: pendingIds })
      },
    })
  }, [selectedRows, bulkDecline])

  const handleBulkUndo = useCallback(() => {
    const reviewedIds = selectedRows
      .filter(row => row.approval_status === 'approved' || row.approval_status === 'blocked')
      .map(row => row.id)

    Modal.confirm({
      title: 'Undo Review',
      content: `Are you sure you want to undo the review for ${reviewedIds.length} offer(s)?`,
      onOk() {
        bulkUndo({ ids: reviewedIds })
      },
    })
  }, [selectedRows, bulkUndo])

  const rowSelection = activeTab === 'offer' ? {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
  } : undefined

  const handleCreateProgram = useCallback(() => {
    openProgramModal()
  }, [openProgramModal])

  const handleEditProgram = useCallback((record) => {
    openProgramModal({ id: record.id })
  }, [openProgramModal])

  const handleDeleteProgram = useCallback((record) => {
    deleteNetworkProgram({ id: record.id })
  }, [deleteNetworkProgram])

  const programHandlers = useMemo(
    () => ({ onEdit: handleEditProgram, onDelete: handleDeleteProgram }),
    [handleEditProgram, handleDeleteProgram]
  )

  const gameColumns = useMemo(() => getGameColumns(programHandlers), [programHandlers])
  const offerColumns = useMemo(() => getOfferColumns(programHandlers), [programHandlers])

  const expandedRowRender = useCallback((record) => {
    const goals = record.goals || []
    if (goals.length === 0) return <span>No goals</span>

    return (
      <AntTable
        columns={goalColumns}
        dataSource={goals}
        rowKey={(goal) => goal.attributes?.goal_id || goal.id}
        pagination={false}
        size="small"
      />
    )
  }, [])

  const isGameTab = activeTab === 'game'

  const renderFilters = () => (
    <FiltersRow key={activeTab}>
      <Search
        placeholder="Search by name, domain, or program ID"
        onSearch={handleSearch}
        style={{ width: 320 }}
        allowClear
        enterButton={<SearchOutlined />}
        defaultValue={search}
      />
      <Select
        mode="multiple"
        placeholder="Network"
        style={{ width: 200 }}
        options={networkOptions}
        onChange={handleNetworkChange}
        allowClear
        maxTagCount={1}
        value={network ? network.split(',') : []}
      />
      <Select
        placeholder="Program Status"
        style={{ width: 180 }}
        options={PROGRAM_STATUS_OPTIONS}
        onChange={handleProgramStatusChange}
        allowClear
        value={programStatus || undefined}
      />
      {!isGameTab && (
        <Select
          placeholder="Approval Status"
          style={{ width: 180 }}
          options={APPROVAL_STATUS_OPTIONS}
          onChange={handleApprovalStatusChange}
          allowClear
          value={approvalStatus || undefined}
        />
      )}
    </FiltersRow>
  )

  const renderBulkActions = () => {
    if (activeTab !== 'offer' || selectedRowKeys.length === 0) return null

    return (
      <BulkActionsRow>
        <Tag>{selectedRowKeys.length} selected</Tag>
        {hasPendingSelected && (
          <>
            <Button
              type="primary"
              icon={<CheckOutlined />}
              onClick={handleBulkApprove}
              loading={isBulkActionLoading}
              size="small"
            >
              Approve
            </Button>
            <Button
              danger
              icon={<CloseOutlined />}
              onClick={handleBulkDecline}
              loading={isBulkActionLoading}
              size="small"
            >
              Decline
            </Button>
          </>
        )}
        {hasReviewedSelected && (
          <Button
            icon={<UndoOutlined />}
            onClick={handleBulkUndo}
            loading={isBulkActionLoading}
            size="small"
          >
            Undo
          </Button>
        )}
      </BulkActionsRow>
    )
  }

  const tableProps = {
    loading: !isLoaded,
    onChange: handleTableChange,
    pagination: {
      current: page,
      pageSize: PER_PAGE,
      total,
      showSizeChanger: false,
      showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} offers`,
    },
    rowKey: 'id',
  }

  return (
    <Wrapper>
      <PageHeader>
        <h2>Earn Offers</h2>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreateProgram}
        >
          Create KarmaEarn Program
        </Button>
      </PageHeader>

      <Tabs activeKey={activeTab} onChange={handleTabChange}>
        <TabPane tab="Game" key="game">
          {renderFilters()}
          <TableWrapper>
            <AntTable
              columns={gameColumns}
              dataSource={data}
              {...tableProps}
              expandable={{
                expandedRowRender,
                rowExpandable: (record) => (record.goals || []).length > 0,
                expandIcon: ({ expanded, onExpand, record }) =>
                  (record.goals || []).length > 0 ? (
                    expanded ? (
                      <DownOutlined onClick={e => { e.stopPropagation(); onExpand(record, e) }} />
                    ) : (
                      <RightOutlined onClick={e => { e.stopPropagation(); onExpand(record, e) }} />
                    )
                  ) : null,
              }}
            />
          </TableWrapper>
        </TabPane>

        <TabPane tab="Offer" key="offer">
          {renderFilters()}
          {renderBulkActions()}
          <TableWrapper>
            <AntTable
              columns={offerColumns}
              dataSource={data}
              rowSelection={rowSelection}
              {...tableProps}
            />
          </TableWrapper>
        </TabPane>
      </Tabs>

    </Wrapper>
  )
}

export default EarnOffersManagement
